import { useEffect, useId, useRef, useState } from 'react';

export function PlayTogetherActionsMenu({ label, onEdit, onRemove }: {
  label: string; onEdit: () => void; onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  useEffect(() => {
    if (!open) return;
    items.current[0]?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);
  const close = () => { setOpen(false); trigger.current?.focus(); };
  return (
    <div ref={root} className="relative shrink-0" onBlur={(e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
    }} onKeyDown={(e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        if (!open) { setOpen(true); return; }
        const current = items.current.findIndex((item) => item === document.activeElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? 1 : (current + (e.key === 'ArrowDown' ? 1 : -1) + 2) % 2;
        items.current[next]?.focus();
      }
    }}>
      <button ref={trigger} type="button" aria-label={`Actions for ${label}`} aria-haspopup="menu"
        aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(!open)}
        className="rounded px-3 py-2 text-ink-2 hover:bg-tint-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep">
        <span aria-hidden="true">•••</span>
      </button>
      {open ? <div id={id} role="menu" aria-label={`Actions for ${label}`} className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-light bg-paper p-1 shadow-sm">
        {[{ label: 'Edit Group', run: onEdit }, { label: 'Remove Group', run: onRemove }].map((action, i) => (
          <button key={action.label} ref={(el) => { items.current[i] = el; }} type="button" role="menuitem" tabIndex={-1}
            className="w-full rounded px-3 py-2 text-left text-[13px] text-ink hover:bg-tint-deep focus-visible:bg-tint-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep"
            onClick={() => { close(); action.run(); }}>{action.label}</button>
        ))}
      </div> : null}
    </div>
  );
}
